import { Cell, Row, Table } from "components/common/Table/Table";
import useLocale from "hooks/common/useLocale/useLocale";
import useTranslate from "hooks/common/useTranslate/useTranslate";
import { TestEnvironment } from "interfaces/test-environments";
import styles from "styles/styles";
import React from "react";
import styled from "styled-components";
import Section from "./Section";
import Status from "./Status";
import { environment as appEnvironment } from "services/common/config/config";

export const Text = styled.p`
    margin: 0;

    :not(:first-child) {
        margin-top: ${styles.spacing[4]};
    }
`;

const EnvBlockHeader = styled.h5`
    font-size: ${styles.fontSize.small};
    line-height: ${styles.fontSize.small};
    margin: 0;
    margin-top: ${styles.spacing[2]};
`;

const EnvBlockText = styled.p`
    font-size: ${styles.fontSize.small};
    margin: 0;

    :not(:first-child) {
        margin-top: ${styles.spacing[4]};
    }
`;

const Container = styled.div`
    background-color: ${styles.colors.grey3};
    padding: ${styles.spacing[4]};
`;

const DetailsText = styled.p`
    font-size: ${styles.fontSize.smaller};
    margin: 0;
`;

const DetailsTable = styled(Table)`
    padding-top: ${styles.spacing[4]};
`;

const Header = styled.h4`
    font-size: ${styles.fontSize.medium};
    line-height: ${styles.fontSize.medium};
    margin: 0;
`;

const Environment = ({ environment }: { environment: TestEnvironment }) => {
    const locale = useLocale();
    const translate = useTranslate();

    const showEnvironmentDetails = appEnvironment === "local" || appEnvironment === "test"; // feature flag

    return (
        <Container>
            <Header>{environment.name[locale]}</Header>
            <Section title={translate("testEnvironments.description")}>
                <Text>{environment.description[locale]}</Text>
            </Section>
            <Section title={translate("testEnvironments.announcement")}>
                {environment.announcements[locale].length ? (
                    environment.announcements[locale].map((announcement, index) => (
                        <Text key={index}>{announcement}</Text>
                    ))
                ) : (
                    <Text>-</Text>
                )}
            </Section>

            <Section title={translate("testEnvironments.environmentStatus")}>
                {showEnvironmentDetails && environment?.apis?.length > 0 && (
                    <Table>
                        {environment.apis.map((api, index) => (
                            <Row key={index}>
                                <Cell>
                                    <Text>{api.name[locale]}</Text>
                                    {api.description && (
                                        <DetailsText>{api.description[locale]}</DetailsText>
                                    )}
                                </Cell>
                                <Cell>
                                    <Status status={api.status} />
                                </Cell>
                            </Row>
                        ))}
                    </Table>
                )}
                {environment.details && (
                    <DetailsTable>
                        {environment.details.version && (
                            <>
                                <Row>
                                    <Cell>
                                        <EnvBlockHeader>
                                            {translate("testEnvironments.version")}:
                                        </EnvBlockHeader>
                                    </Cell>
                                </Row>
                                <Row>
                                    <Cell>
                                        <EnvBlockText>{environment.details.version}</EnvBlockText>
                                    </Cell>
                                </Row>
                            </>
                        )}
                        {environment.details.schema && (
                            <>
                                <Row>
                                    <Cell>
                                        <EnvBlockHeader>
                                            {translate("testEnvironments.schema")}:
                                        </EnvBlockHeader>
                                    </Cell>
                                </Row>
                                <Row>
                                    <Cell>
                                        <EnvBlockText>{environment.details.schema}</EnvBlockText>
                                    </Cell>
                                </Row>
                            </>
                        )}
                        <>
                            {environment.details.gui && (
                                <>
                                    <Row>
                                        <Cell>
                                            <EnvBlockHeader>
                                                {translate("testEnvironments.gui")}:
                                            </EnvBlockHeader>
                                        </Cell>
                                    </Row>
                                    <Row>
                                        <Cell>
                                            <EnvBlockText>{environment.details.gui}</EnvBlockText>
                                        </Cell>
                                    </Row>
                                </>
                            )}
                            {environment.details.webClient && (
                                <>
                                    <Row>
                                        <Cell>
                                            <EnvBlockHeader>
                                                {translate("testEnvironments.webClient")}:
                                            </EnvBlockHeader>
                                        </Cell>
                                    </Row>
                                    <Row>
                                        <Cell>
                                            <EnvBlockText>
                                                {environment.details.webClient}
                                            </EnvBlockText>
                                        </Cell>
                                    </Row>
                                </>
                            )}
                            {environment.details.cap && (
                                <>
                                    <Row>
                                        <Cell>
                                            <EnvBlockHeader>
                                                {translate("testEnvironments.cap")}:
                                            </EnvBlockHeader>
                                        </Cell>
                                    </Row>
                                    <Row>
                                        <Cell>
                                            <EnvBlockText>{environment.details.cap}</EnvBlockText>
                                        </Cell>
                                    </Row>
                                </>
                            )}
                            {environment.details.meteringDataToken && (
                                <>
                                    <Row>
                                        <Cell>
                                            <EnvBlockHeader>
                                                {translate("testEnvironments.meteringDataToken")}:
                                            </EnvBlockHeader>
                                        </Cell>
                                    </Row>
                                    <Row>
                                        <EnvBlockText>
                                            <Text>{environment.details.meteringDataToken}</Text>
                                        </EnvBlockText>
                                    </Row>
                                </>
                            )}
                            {environment.details.meteringDataDelivery && (
                                <>
                                    <Row>
                                        <Cell>
                                            <EnvBlockHeader>
                                                {translate("testEnvironments.meteringDataDelivery")}
                                                :
                                            </EnvBlockHeader>
                                        </Cell>
                                    </Row>
                                    <Row>
                                        <Cell>
                                            <EnvBlockText>
                                                {environment.details.meteringDataDelivery}
                                            </EnvBlockText>
                                        </Cell>
                                    </Row>
                                </>
                            )}
                        </>
                    </DetailsTable>
                )}
            </Section>
        </Container>
    );
};

export default Environment;
